"use client";

import { useGlobalContent } from "@app/[locale]/GlobalContentProvider";
import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import { useAuth } from "@lib/auth/useAuth";
import {
    checkoutPage,
    checkoutV2Page,
    getPagePathByLocale,
    orderConfirmationPage,
    shoppingBagPage,
} from "@lib/constants/pagePaths";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";
import { isEmpty } from "lodash-es";
import { useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import { useCallback, useEffect } from "react";
import { BLOOMREACH_LEAD_CAPTURE_EVENT, BLOOMREACH_LEAD_CAPTURE_SESSION_KEY } from "../constants";

const EXCLUDED_PAGES = [shoppingBagPage, checkoutPage, checkoutV2Page, orderConfirmationPage];

const isBloomreachLeadCapturePayload = (
    payload: BloomreachLeadCapturePayload | undefined
): payload is BloomreachLeadCapturePayload => Boolean(payload?.customer_ids?.cookie);

export const BloomreachLeadCaptureController = () => {
    const locale = useLocale();
    const pathname = usePathname();
    const { siteSettings } = useGlobalContent();
    const { isLoading, isLoggedIn } = useAuth();
    const { isReady, showModal } = useGlobalModalContext();
    const isLeadCapturePopupEnabled = useFeatureFlag("isLeadCapturePopupEnabled");

    const isExcludedPage = EXCLUDED_PAGES.map((pagePath) =>
        getPagePathByLocale(pagePath, locale)
    ).some((path) => pathname.includes(path));
    const newsletterPolicy = siteSettings.auth.newsletter;
    const isNewsletterPolicyMissing =
        !newsletterPolicy || isEmpty(Object.values(newsletterPolicy).filter(Boolean));
    const canShowLeadCapture =
        isLeadCapturePopupEnabled &&
        isReady &&
        !isLoading &&
        !isLoggedIn &&
        !isExcludedPage &&
        !isNewsletterPolicyMissing;

    const showLeadCapture = useCallback(
        (payload: BloomreachLeadCapturePayload | undefined) => {
            if (!isBloomreachLeadCapturePayload(payload)) return;
            // Keep the pending payload while auth and the modal system are still
            // resolving: the mount-time read of the global exists only to cover
            // the tag-before-React race.
            if (!isReady || isLoading) return;

            // Any settled outcome consumes the signal. Replaying a retained
            // payload after a later navigation or logout would move Bloomreach's
            // show/timing decision into the frontend.
            window.bloomreachManagedEndpointPayload = undefined;

            if (!canShowLeadCapture) return;
            if (sessionStorage.getItem(BLOOMREACH_LEAD_CAPTURE_SESSION_KEY)) return;

            sessionStorage.setItem(BLOOMREACH_LEAD_CAPTURE_SESSION_KEY, "true");
            showModal(ModalTypes.newsletterSignup);
        },
        [canShowLeadCapture, isLoading, isReady, showModal]
    );

    useEffect(() => {
        const handleEligibility = (event: WindowEventMap[typeof BLOOMREACH_LEAD_CAPTURE_EVENT]) => {
            showLeadCapture(event.detail);
        };

        window.addEventListener(BLOOMREACH_LEAD_CAPTURE_EVENT, handleEligibility);
        showLeadCapture(window.bloomreachManagedEndpointPayload);

        return () => {
            window.removeEventListener(BLOOMREACH_LEAD_CAPTURE_EVENT, handleEligibility);
        };
    }, [showLeadCapture]);

    return null;
};
